import { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Calendar, User, Tag, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import DOMPurify from 'dompurify';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Blog List Page
export const BlogList = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPosts = useCallback(async () => {
    try {
      const url = selectedCategory 
        ? `${API}/blog?category=${selectedCategory}`
        : `${API}/blog`;
      const response = await axios.get(url);
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/blog/categories/list`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  }, []);

  useEffect(() => {
    fetchPosts();
    fetchCategories();
  }, [fetchPosts, fetchCategories]);

  const getCategoryLabel = (id) => {
    const cat = categories.find(c => c.id === id);
    return cat ? cat.name : id;
  };

  return (
    <div className="min-h-screen bg-[#FDFBF8]" data-testid="blog-list-page">
      {/* Header */}
      <div className="bg-[#1E2A4A] py-16">
        <div className="container-custom">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-4 text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-medium text-white">
            Health & <em className="text-[#4A6FA5] italic">Wellness</em> Blog
          </h1>
          <p className="text-[#E8EEF5]/60 mt-3 max-w-xl">
            Expert insights on orthopaedic care, joint health, sports injuries, and recovery tips from Dr. Drumil D Majmundar.
          </p>
        </div>
      </div>

      <div className="container-custom py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <h3 className="text-xs uppercase tracking-wider text-[#7A8FA0] mb-4">Categories</h3>
              <div className="space-y-2">
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={`w-full text-left px-4 py-2 rounded-lg text-sm transition-colors ${
                    !selectedCategory 
                      ? 'bg-[#4A6FA5] text-white' 
                      : 'bg-[#F5F7FA] text-[#1E2A4A] hover:bg-[#E8EEF5]'
                  }`}
                >
                  All Posts
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`w-full text-left px-4 py-2 rounded-lg text-sm transition-colors flex justify-between items-center ${
                      selectedCategory === cat.id 
                        ? 'bg-[#4A6FA5] text-white' 
                        : 'bg-[#F5F7FA] text-[#1E2A4A] hover:bg-[#E8EEF5]'
                    }`}
                  >
                    <span>{cat.name}</span>
                    <span className={`text-xs ${selectedCategory === cat.id ? 'text-white/60' : 'text-[#7A8FA0]'}`}>
                      {cat.count}
                    </span>
                  </button>
                ))}
              </div>

              {/* CTA */}
              <div className="mt-8 p-6 bg-[#1E2A4A] rounded-2xl text-center">
                <h4 className="font-['Cormorant_Garamond'] text-lg text-[#E8EEF5] mb-2">
                  Have Questions?
                </h4>
                <p className="text-xs text-[#E8EEF5]/50 mb-4">
                  Book a consultation with Dr. Majmundar
                </p>
                <Link to="/book-appointment">
                  <Button className="w-full bg-[#4A6FA5] hover:bg-[#3d5d8a] text-white">
                    Book Appointment
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* Posts Grid */}
          <div className="lg:col-span-3">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="bg-white rounded-2xl overflow-hidden animate-pulse">
                    <div className="h-48 bg-[#F5F7FA]" />
                    <div className="p-6 space-y-3">
                      <div className="h-4 bg-[#F5F7FA] rounded w-1/4" />
                      <div className="h-6 bg-[#F5F7FA] rounded w-3/4" />
                      <div className="h-4 bg-[#F5F7FA] rounded w-full" />
                    </div>
                  </div>
                ))}
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-[#7A8FA0]">No posts found in this category.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {posts.map((post, index) => (
                  <motion.div
                    key={post.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link to={`/blog/${post.slug}`}>
                      <article className="blog-card bg-white rounded-2xl overflow-hidden group">
                        <div className="h-48 overflow-hidden">
                          <img
                            src={post.image_url || 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=400&h=300&fit=crop'}
                            alt={post.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                        <div className="p-6">
                          <Badge variant="outline" className="mb-3 text-[10px] uppercase tracking-wider text-[#4A6FA5] border-[#4A6FA5]/30">
                            {getCategoryLabel(post.category)}
                          </Badge>
                          <h2 className="font-['Cormorant_Garamond'] text-xl font-semibold text-[#1E2A4A] mb-2 group-hover:text-[#4A6FA5] transition-colors line-clamp-2">
                            {post.title}
                          </h2>
                          <p className="text-sm text-[#7A8FA0] mb-4 line-clamp-2">
                            {post.excerpt}
                          </p>
                          <div className="flex items-center gap-4 text-xs text-[#7A8FA0]">
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {post.author}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {format(new Date(post.created_at), 'MMM d, yyyy')}
                            </span>
                          </div>
                        </div>
                      </article>
                    </Link>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Single Blog Post Page
export const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPost = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/blog/${slug}`);
      setPost(response.data);
    } catch (error) {
      console.error('Failed to fetch post:', error);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchPost();
  }, [fetchPost]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF8] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#4A6FA5] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-[#FDFBF8] flex items-center justify-center" data-testid="blog-not-found">
        <div className="text-center">
          <h2 className="font-['Cormorant_Garamond'] text-2xl text-[#1E2A4A] mb-4">
            Post Not Found
          </h2>
          <Link to="/blog">
            <Button variant="outline">Back to Blog</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF8]" data-testid="blog-post-page">
      {/* Header Image */}
      <div className="relative h-[40vh] min-h-[300px] bg-[#1E2A4A]">
        <img
          src={post.image_url || 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=1200&h=600&fit=crop'}
          alt={post.title}
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1E2A4A] to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 container-custom pb-8">
          <Link 
            to="/blog" 
            className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-4 text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>
          <Badge variant="outline" className="mb-4 text-[10px] uppercase tracking-wider text-[#4A6FA5] border-[#4A6FA5]/30 bg-white/10">
            {post.category.replace('-', ' ')}
          </Badge>
          <h1 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl lg:text-5xl font-medium text-white max-w-3xl">
            {post.title}
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="container-custom py-12">
        <div className="max-w-3xl mx-auto">
          {/* Meta */}
          <div className="flex flex-wrap items-center gap-6 mb-8 pb-8 border-b border-[#E8EEF5]">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1E2A4A] to-[#4A6FA5] flex items-center justify-center text-white font-medium">
                D
              </div>
              <div>
                <p className="text-sm font-medium text-[#1E2A4A]">{post.author}</p>
                <p className="text-xs text-[#7A8FA0]">MS Orthopaedics</p>
              </div>
            </div>
            <div className="flex items-center gap-1 text-sm text-[#7A8FA0]">
              <Calendar className="w-4 h-4" />
              {format(new Date(post.created_at), 'MMMM d, yyyy')}
            </div>
            <div className="flex items-center gap-1 text-sm text-[#7A8FA0]">
              <Clock className="w-4 h-4" />
              5 min read
            </div>
          </div>

          {/* Excerpt */}
          <p className="text-lg text-[#5e7080] leading-relaxed mb-8 font-medium">
            {post.excerpt}
          </p>

          {/* Content */}
          <div 
            className="prose prose-lg max-w-none
              prose-headings:font-['Cormorant_Garamond'] prose-headings:text-[#1E2A4A] prose-headings:font-semibold
              prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
              prose-p:text-[#5e7080] prose-p:leading-relaxed
              prose-strong:text-[#1E2A4A] prose-strong:font-semibold
              prose-ul:text-[#5e7080] prose-li:marker:text-[#4A6FA5]
              prose-a:text-[#4A6FA5] prose-a:no-underline hover:prose-a:underline
            "
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }}
          />

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="mt-10 pt-8 border-t border-[#E8EEF5]">
              <div className="flex items-center gap-2 flex-wrap">
                <Tag className="w-4 h-4 text-[#7A8FA0]" />
                {post.tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="bg-[#F5F7FA] text-[#1E2A4A]">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="mt-12 p-8 bg-[#1E2A4A] rounded-2xl text-center">
            <h3 className="font-['Cormorant_Garamond'] text-2xl text-[#E8EEF5] mb-2">
              Need Expert Orthopaedic Care?
            </h3>
            <p className="text-sm text-[#E8EEF5]/50 mb-6 max-w-md mx-auto">
              Book a consultation with Dr. Drumil D Majmundar for personalised treatment plans.
            </p>
            <Link to="/book-appointment">
              <Button className="bg-[#4A6FA5] hover:bg-[#3d5d8a] text-white px-8">
                Book Appointment
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
